/*
  Elixir product form, bridged to Horizon's cart.

  Elixir's product-form.js posts to /cart/add and then hands the response to an Elixir
  <cart-drawer>. Horizon has no such element, so stock Elixir would redirect to /cart. This keeps
  Elixir's button behaviour (spinner, inline errors, sold out) and swaps only the submit path for
  the one Horizon's own product-form.js uses:

  1. The same FormData POST to /cart/add.js through window.fetch, looked up at call time, so the
     free-winder hook in header-group (rv_winder_autoadd, which wraps window.fetch) still adds
     the winder in the same request.
  2. Horizon's shopify:cart:lines-update event (CartLinesUpdateEvent), resolved with the fresh
     cart and the re-rendered cart sections, so Horizon's drawer opens, the count bubble updates
     and the line items morph in.

  The sticky bar clicks the main button, so it takes this same path.
*/
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const cartAddUrl = () => window.Theme?.routes?.cart_add_url || `${window.routes?.cart_add_url || '/cart/add'}.js`;
const cartUrl = () => window.Theme?.routes?.cart_url || window.routes?.cart_url || '/cart';

async function refreshCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const response = await fetch(`${cartUrl()}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/*
  Line-item properties (the gift card recipient form) only survive /cart/add.js as
  items[0][properties][...]. The header-group winder hook rewrites any plain `id` body into
  items[] and drops top-level properties[...], so a body that carries properties is sent in
  items[] form already. The hook leaves an items[] body alone, which also keeps the free watch
  winder off gift card orders. Bodies without properties (every watch) are untouched, so the
  winder still rides along on watches.
*/
function itemsWithProperties(formData) {
  const keys = [...formData.keys()].filter((key) => key.startsWith('properties['));
  if (!keys.length || !formData.get('id')) return formData;
  const out = new FormData();
  out.append('items[0][id]', formData.get('id'));
  out.append('items[0][quantity]', formData.get('quantity') || '1');
  for (const [key, value] of formData.entries()) {
    // properties[Recipient email] -> items[0][properties][Recipient email]
    if (key.startsWith('properties[')) out.append(`items[0][${key.replace('[', '][')}`, value);
    else if (!['id', 'quantity', 'form_type', 'utf8'].includes(key)) out.append(key, value);
  }
  return out;
}

class ExProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton?.querySelector('.button-text') || null;
    this.hideErrors = this.dataset.hideErrors === 'true';
    const idInput = this.form.querySelector('[name=id]');
    if (idInput) idInput.disabled = false;
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  onSubmit = (event) => {
    event.preventDefault();
    if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;
    this.add(new FormData(this.form));
  };

  async add(formData) {
    this.showError(false);
    this.setLoading(true);

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));
    if (!formData.get('selling_plan')) formData.delete('selling_plan');
    formData = itemsWithProperties(formData);

    const variantId = String(formData.get('id') || '');
    const quantity = Number(formData.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await window.fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData,
      });
      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        this.showError(data.description || data.message);
        const cart = await refreshCart().catch(() => null);
        deferred.resolve({
          cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
          detail: { didError: true, source: 'ex-product-form', itemCount: quantity },
        });
        return;
      }

      const cart = await refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ex-product-form',
          sourceId: this.id || 'ex-product-form',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
      document.dispatchEvent(new CustomEvent('ex:cart:added', { detail: { variantId, cart } }));
    } catch (error) {
      console.warn('[ex-product-form] add to cart failed:', error);
      deferred.reject(error);
      this.showError(window.cartStrings?.error || 'Something went wrong. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    const spinner = this.querySelector('.loading__spinner');
    if (isLoading) {
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      spinner?.classList.remove('hidden');
    } else {
      this.submitButton.removeAttribute('aria-disabled');
      this.submitButton.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  }

  showError(message) {
    if (this.hideErrors) return;
    const wrapper = this.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) return;
    wrapper.toggleAttribute('hidden', !message);
    const text = wrapper.querySelector('.product-form__error-message');
    if (message && text) text.textContent = message;
  }
}

if (!customElements.get('ex-product-form')) customElements.define('ex-product-form', ExProductForm);

